import * as React from 'react';

import styled, { css } from 'styled-components';

import { Translate } from 'react-localize-redux';

import Flex from '@components/layout/flex';
import Typography from '@components/basics/typography';

export interface OwnershipBarItemProps {
  className?: string;
  percentage?: number;
  newPercentage?: number;
  barWidth?: number;
  barComplete?: boolean;
  name?: string;
  isGrey?: boolean;
  isYellow?: boolean;
  isRed?: boolean;
  isYou?: boolean;
  isNew?: boolean;
  hasHigherSplit?: boolean;
}

const MAX_PERCENTAGE = 9;

const MARGIN = '1px';

export const OwnershipBarItem: React.FC<OwnershipBarItemProps> = (props) => {
  return (
    <Flex
      flexDirection="column"
      className={`${props.className} ${props.isGrey ? 'grey' : ''} ${
        props.isYellow ? 'yellow' : ''
      } ${props.isRed ? 'red' : ''}`}
    >
      {(props.percentage && props.percentage > MAX_PERCENTAGE) ||
      (props.percentage === 0 && props.isRed) ||
      props.isNew ? (
        <PercentageTypography type="small">
          {props.newPercentage
            ? `${props.percentage}% → ${props.newPercentage}`
            : props.isNew
            ? '—'
            : `${props.percentage}%`}
        </PercentageTypography>
      ) : (
        <PercentageTypography type="small"> </PercentageTypography>
      )}
      <Flex>
        <Bar
          className={`bar ${props.isGrey ? 'grey' : ''} ${
            props.isYellow ? 'yellow' : ''
          } ${props.hasHigherSplit ? 'blue' : 'grey'} ${props.isRed ? 'red' : ''}`}
          percentage={props.percentage}
        />
        {props.percentage && props.newPercentage && (
          <Bar
            className="yellow"
            percentage={props.newPercentage}
            style={{ maxWidth: `${props.newPercentage}%` }}
          />
        )}
      </Flex>
      {props.percentage && props.percentage > MAX_PERCENTAGE ? (
        <NameTypography className={props.isYou ? 'blue' : ''} weight="700">
          {props.isYou ? <Translate id="COMMON.YOU" /> : props.name || '.'}
        </NameTypography>
      ) : (
        <NameTypography weight="700"> </NameTypography>
      )}
    </Flex>
  );
};

export default styled(OwnershipBarItem)`
  flex-grow: ${(props) =>
    props.percentage && props.barComplete
      ? props.percentage <= MAX_PERCENTAGE
        ? '0'
        : '1'
      : '0'};
  width: ${(props) =>
    props.percentage
      ? props.percentage <= MAX_PERCENTAGE
        ? '3px'
        : css`calc(${props.percentage}% - ${MARGIN})`
      : '100%'};

  span {
    color: ${({ theme }) => theme.colors.blueColor};
  }

  &.grey {
    span {
      color: ${({ theme }) => theme.colors.primary};
    }
  }
  &.yellow {
    span {
      color: ${({ theme }) => theme.colors.upload};
    }
  }
  &.red {
    span {
      color: ${({ theme }) => theme.colors.error};
    }
  }
`;

const PercentageTypography = styled(Typography)`
  min-width: 1px;
`;

const NameTypography = styled(Typography)`
  min-width: 1px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;

  &.blue {
    color: ${({ theme }) => theme.colors.blueColor};
  }
`;

const Bar = styled.span<OwnershipBarItemProps>`
  height: 5px;
  background-color: ${({ theme }) => theme.colors.blueColor};
  display: block;
  margin: 5px 0;
  width: 100%;
  flex-grow: 1;

  &.grey {
    background-color: ${({ theme }) => theme.colors.primary};
  }

  &.yellow {
    background-color: ${({ theme }) => theme.colors.upload};
    opacity: 1;
  }

  &.blue {
    opacity: 1;
  }

  &.red {
    opacity: 1;
    background-color: ${({ theme }) => theme.colors.error};
    width: 3px;
  }

  ${(props) =>
    props.percentage && props.percentage <= MAX_PERCENTAGE
      ? css`
          opacity: 0.1;
        `
      : null}

  ${(props) =>
    props.percentage &&
    props.percentage > MAX_PERCENTAGE &&
    props.percentage <= 20
      ? css`
          opacity: 0.2;
        `
      : null}

  ${(props) =>
    props.percentage && props.percentage > 20
      ? css`
          opacity: 0.3;
        `
      : null}

  ${(props) =>
    props.percentage && props.percentage > 40
      ? css`
          opacity: 0.5;
        `
      : null}

  ${(props) =>
    props.percentage && props.percentage > 70
      ? css`
          opacity: 0.8;
        `
      : null}

  ${(props) =>
    props.percentage && props.percentage === 100
      ? css`
          opacity: 1;
        `
      : null}
`;
